/**
 * A labelled slider with its number beside it, and a step either way.
 *
 * The effect controls are all of this one shape, as they are in Canva: drag
 * for feel, type for exactness, tap the ends for one step. The three reach the
 * same value, and a drag is one undo step because the caller commits with a
 * shared key.
 */

import { useId } from 'react';
import { useEditor } from '../state/store';
import { fromMm, stepIn, toMm, unitInfo } from '../model/units';

interface Props {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  /** Decimals in the number box. Whole numbers unless a length needs more. */
  decimals?: number;
  onChange: ( value: number ) => void;
}

export function Slider( { label, value, min = 0, max = 100, step = 1, decimals = 0, onChange }: Props ) {
  const id = useId();
  const clamp = ( v: number ) => Math.min( max, Math.max( min, v ) );
  const scale = Math.pow( 10, decimals );
  const shown = Math.round( value * scale ) / scale;
  const nudge = ( v: number ) => clamp( Math.round( v * scale ) / scale );

  // How far along the track the value sits, for the filled part of it.
  const share = ( ( value - min ) / ( max - min || 1 ) ) * 100;

  return (
    <div className="tb-slider">
      <label className="tb-slider__label" htmlFor={ id }>
        { label }
      </label>

      <div className="tb-slider__row">
        <input
          id={ id }
          className="tb-slider__track"
          type="range"
          min={ min }
          max={ max }
          step={ step }
          value={ value }
          style={ { '--tb-share': `${ share }%` } as React.CSSProperties }
          onChange={ ( event ) => onChange( clamp( parseFloat( event.target.value ) ) ) }
        />

        <div className="tb-slider__box">
          <button
            type="button"
            className="tb-slider__step"
            aria-label={ `Less ${ label.toLowerCase() }` }
            onClick={ () => onChange( nudge( shown - step ) ) }
          >
            −
          </button>
          <input
            className="tb-slider__number tb-num"
            type="number"
            inputMode={ decimals ? 'decimal' : 'numeric' }
            min={ min }
            max={ max }
            step={ step }
            value={ shown }
            aria-label={ label }
            onChange={ ( event ) => {
              const next = parseFloat( event.target.value );

              if ( ! Number.isNaN( next ) ) onChange( clamp( next ) );
            } }
          />
          <button
            type="button"
            className="tb-slider__step"
            aria-label={ `More ${ label.toLowerCase() }` }
            onClick={ () => onChange( nudge( shown + step ) ) }
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A slider over a length, in the customer's own unit.
 *
 * Takes and gives millimetres, so the caller never knows the customer thinks in
 * inches; the label says the unit, and the step is about a tenth of a
 * millimetre in whatever that is.
 */
export function LengthSlider( { label, mm, maxMm, minMm = 0, onChange }: {
  label: string;
  mm: number;
  maxMm: number;
  minMm?: number;
  onChange: ( mm: number ) => void;
} ) {
  const unit = useEditor( ( s ) => s.unit );
  const step = stepIn( 0.1, unit );
  const decimals = Math.max( 0, Math.ceil( -Math.log10( step ) - 1e-9 ) );

  return (
    <Slider
      label={ `${ label } (${ unitInfo( unit ).short })` }
      value={ fromMm( mm, unit ) }
      min={ fromMm( minMm, unit ) }
      max={ fromMm( maxMm, unit ) }
      step={ step }
      decimals={ decimals }
      onChange={ ( v ) => onChange( toMm( v, unit ) ) }
    />
  );
}
